// src/components/inventory/ReceiveGoodsModal.js
// --- UPDATE (Procurement MOW+ v1) — NEW FILE ---------------------------------------------
// Goods receipt for a purchase order — all or part of each line. Shows ordered / received /
// still to come; quantities default to what is outstanding and cannot exceed it. Saving books the
// stock through the inventory movement engine (history, movements, live update) with the PO
// number, and the PO becomes "Partly received" or "Received". FlowSense shows the stock and days of
// cover each line will give.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Button, Table, Thead, Tbody, Tr, Th, Td, NumberInput, NumberInputField,
    Text, HStack, VStack, Badge, Alert, AlertIcon, Input, FormControl, FormLabel, Progress, Icon, useColorModeValue, Skeleton,
} from '@chakra-ui/react';
import { MdInventory, MdAutoAwesome } from 'react-icons/md';
import purchaseOrderService, { poErrorMessage } from '../../services/purchaseOrderService';

function ReceiveGoodsModal({ isOpen, onClose, po, insights, onDone }) {
    const [lines, setLines] = useState(null);
    const [qty, setQty] = useState({});
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const muted = useColorModeValue('gray.500', 'gray.400');
    const aiBg = useColorModeValue('teal.50', 'whiteAlpha.100');

    useEffect(() => {
        if (!isOpen || !po) return undefined;
        let alive = true;
        setLines(null); setError(null); setNote('');
        (async () => {
            try {
                const r = po.receiving ? { lines: po.receiving } : await purchaseOrderService.getReceipts(po.id);
                if (!alive) return;
                setLines(r.lines || []);
                setQty(Object.fromEntries((r.lines || []).map((l) => [l.purchase_order_item_id, l.remaining > 0 ? String(l.remaining) : '0'])));
            } catch (e) { if (alive) { setError(poErrorMessage(e)); setLines([]); } }
        })();
        return () => { alive = false; };
    }, [isOpen, po]);

    const aiById = useMemo(() => new Map((insights || []).map((i) => [String(i.id), i])), [insights]);
    const want = (lines || []).map((l) => ({ ...l, take: Number(qty[l.purchase_order_item_id]) || 0 }));
    const invalid = want.some((l) => l.take < 0 || l.take > l.remaining || !Number.isFinite(l.take)) || !want.some((l) => l.take > 0);
    const all = want.length > 0 && want.every((l) => l.take === l.remaining);

    const submit = async () => {
        setBusy(true); setError(null);
        try {
            const r = await purchaseOrderService.receivePO(po.id, want.filter((l) => l.take > 0).map((l) => ({ purchase_order_item_id: l.purchase_order_item_id, quantity: l.take })), note.trim() || undefined);
            onDone?.(r); onClose();
        } catch (e) { setError(poErrorMessage(e)); } finally { setBusy(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl">
                <ModalHeader><HStack><Icon as={MdInventory} color="green.500" /><Text>Receive goods</Text></HStack>
                    <Text fontSize="sm" color={muted} fontWeight="normal">{po?.po_number || (po ? `PO #${po.id}` : '')} · {po?.supplier_name}</Text></ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
                        {lines === null ? <Skeleton h="120px" borderRadius="lg" /> : (
                            <Table size="sm" aria-label="Lines to receive">
                                <Thead><Tr><Th>Item</Th><Th isNumeric>Ordered</Th><Th isNumeric>Received</Th><Th isNumeric>To come</Th><Th isNumeric>Receive now</Th><Th>FlowSense</Th></Tr></Thead>
                                <Tbody>{want.map((l) => {
                                    const ai = aiById.get(String(l.item_id));
                                    const after = ai ? Number(ai.quantity) + l.take : null;
                                    return (
                                        <Tr key={l.purchase_order_item_id}>
                                            <Td><Text fontWeight="700" fontSize="sm">{l.item_name || `Item ${l.item_id}`}</Text>
                                                <Progress mt={1} size="xs" borderRadius="full" colorScheme="green" value={l.ordered ? ((l.received + l.take) / l.ordered) * 100 : 0} /></Td>
                                            <Td isNumeric>{l.ordered}</Td><Td isNumeric>{l.received}</Td>
                                            <Td isNumeric>{l.remaining > 0 ? l.remaining : <Badge colorScheme="green">done</Badge>}</Td>
                                            <Td isNumeric><NumberInput size="sm" maxW="100px" min={0} max={l.remaining} value={qty[l.purchase_order_item_id] ?? ''} isDisabled={l.remaining <= 0}
                                                onChange={(v) => setQty((x) => ({ ...x, [l.purchase_order_item_id]: v }))}><NumberInputField aria-label={`Receive ${l.item_name || l.item_id}`} /></NumberInput>
                                                {l.take > l.remaining && <Text fontSize="2xs" color="red.500">max {l.remaining}</Text>}</Td>
                                            <Td fontSize="xs" color={muted}>{ai ? `${ai.quantity} → ${after} in stock${ai.avg_daily_usage > 0 ? ` · ${Math.round(after / ai.avg_daily_usage)} d cover` : ''}` : '—'}</Td>
                                        </Tr>
                                    );
                                })}</Tbody>
                            </Table>
                        )}
                        <FormControl><FormLabel fontSize="sm">Delivery note / reference</FormLabel><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. DN-4471, truck 1 of 2" maxLength={200} aria-label="Delivery note" /></FormControl>
                        <HStack bg={aiBg} p={2.5} borderRadius="lg" spacing={2} align="start"><Icon as={MdAutoAwesome} color="teal.400" mt={0.5} />
                            <Text fontSize="sm">{all ? 'Everything outstanding will be received — the order closes as Received.' : 'Part of the order will be received — it stays open as "Partly received" until the rest arrives.'} Stock is booked with the PO number in the audit history.</Text></HStack>
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button colorScheme="green" onClick={submit} isLoading={busy} isDisabled={invalid || lines === null}>{all ? 'Receive all' : 'Receive selected'}</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
ReceiveGoodsModal.propTypes = { isOpen: PropTypes.bool, onClose: PropTypes.func.isRequired, po: PropTypes.object, insights: PropTypes.array, onDone: PropTypes.func };
export default ReceiveGoodsModal;
